import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Salon, Staff, UpdateSalon } from '@salonomy/types';
import { api, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

export function useSalon() {
  return useQuery({
    queryKey: qk.salon,
    queryFn: async () => unwrap(await api.GET('/v1/salon')),
  });
}

export function useUpdateSalon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateSalon): Promise<Salon> =>
      // Friendly @salonomy/types body; the generated type over-requires default-valued fields,
      // so we cast at the boundary — the server re-validates against the same Zod schema.
      unwrap(await api.PATCH('/v1/salon', { body: body as never })),
    onSuccess: (salon) => qc.setQueryData(qk.salon, salon),
  });
}

export function useStaff() {
  return useQuery({
    queryKey: qk.staff,
    queryFn: async (): Promise<Staff[]> => unwrap(await api.GET('/v1/staff')),
  });
}

/** Active staff who take appointments — the diary's chairs (owner/stylist/chair_renter). */
export function chairStaff(staff: Staff[] | undefined): Staff[] {
  if (!staff) return [];
  return staff
    .filter((s) => s.active && ['owner', 'stylist', 'chair_renter'].includes(s.role))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}
